/**
 * Run `task` over `items` with at most `limit` in flight, preserving input order in the result.
 *
 * `Promise.all` is the wrong tool when each task uploads a file: a dozen multi-megabyte multipart
 * requests fired at once saturate the uplink and nothing completes until most of them do. A pool
 * keeps the first results arriving early so the UI can report progress.
 *
 * Rejections propagate, exactly as `Promise.all` would — a caller that wants per-item failures
 * should resolve them inside `task`.
 */
export async function asyncPool<TItem, TResult>(items: TItem[], limit: number, task: (item: TItem, index: number) => Promise<TResult>): Promise<TResult[]> {
	if (limit < 1) throw new Error(`asyncPool: limit must be at least 1, got ${limit}`);

	const results = new Array<TResult>(items.length);
	let cursor = 0;

	const worker = async (): Promise<void> => {
		while (cursor < items.length) {
			const index = cursor;
			cursor += 1;
			results[index] = await task(items[index], index);
		}
	};

	await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
	return results;
}
